import axios from 'axios'
import { MessageBox, Message } from 'element-ui' // 使用 element-ui 弹窗提示
import { getToken, removeToken } from '@/utils/auth'

// 根据环境自动选择后端地址
// 开发环境使用相对路径，通过 devServer proxy 转发到后端
// 正式构建默认直连线上 API；部署环境可覆盖为同源路径或独立 API 地址。
const baseURLPy = (process.env.VUE_APP_API_PY || (process.env.NODE_ENV === 'development'
  ? ''
  : 'https://api.xingyuwanxiang.com')).replace(/\/+$/, '')

const baseURLGo = (process.env.VUE_APP_API_GO || (process.env.NODE_ENV === 'development'
  ? ''
  : 'https://api.xingyuwanxiang.com')).replace(/\/+$/, '')

const baseURLNW = (process.env.VUE_APP_API_NW
  || (process.env.NODE_ENV === 'development' ? 'http://127.0.0.1:8100' : 'https://api.xingyuwanxiang.com')).replace(/\/+$/, '')


const service = axios.create({
  baseURL: baseURLPy,
  timeout: 300000
})

export const serviceGo = axios.create({
  baseURL: baseURLGo,
  timeout: 300000
})

export const serviceNW = axios.create({
  baseURL: baseURLNW,
  timeout: 600000
})

// novelweaver-api 使用固定 API Key 鉴权
serviceNW.interceptors.request.use(
  config => {
    const apiKey = process.env.VUE_APP_NW_API_KEY
    if (apiKey) {
      config.headers.Authorization = `Bearer ${apiKey}`
    }
    return config
  },
  error => Promise.reject(error)
)

// ===== 401 统一处理 =====
// token 过期后后端所有接口都会返回 401，这里统一清登录态并跳登录页。
// 不做的话页面只会空着 + 每个失败接口弹一个「Invalid token」，用户看不懂也不知道要重新登录。
//
// 但**只有确认 token 真的过期了才会把人踢下线**（见 tokenExpired）。后端的 401 不止「过期」
// 一种成因（缺 Authorization 头、claims 异常、用户查不到……，见 Go 的 middleware/auth.go），
// 一律登出会让人莫名其妙被弹回登录页。权限不足后端返的是 403，不走这里。
const AUTH_PAGES = ['/login', '/wx-login-callback', '/qq-login-callback']

// 一个页面常并发好几个请求，401 会一起回来；不去重就会叠出好几个弹窗
let handlingUnauthorized = false

// tokenExpired 就地解 JWT 的 exp 判断是否已过期。
// 留 5 分钟余量：这里只在「已经收到 401」时才调用，此时若 token 马上就要到期，
// 基本就是客户端与服务端时钟有偏差，按过期处理比让用户对着一堆报错强。
// 解不出来（格式坏了/不是 JWT）也算过期——这种 token 本来就没法用了。
const EXPIRY_SKEW_MS = 5 * 60 * 1000

function tokenExpired(token: string): boolean {
  try {
    const payload = token.split('.')[1]
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((payload.length + 3) % 4))
    const exp = JSON.parse(json).exp
    // 没有 exp 字段 = 无从判断是否过期，那这个 401 就不是过期引起的，别动登录态
    if (typeof exp !== 'number') return false
    return exp * 1000 <= Date.now() + EXPIRY_SKEW_MS
  } catch {
    return true
  }
}

function handleUnauthorized(): boolean {
  // 登录页自身的接口（微信/QQ 登录）返回 401 是「登录失败」，不是「登录过期」，交给页面自己处理
  if (AUTH_PAGES.some(p => window.location.pathname.startsWith(p))) return false

  // 注意顺序：这个判断必须在 getToken() 之前。首个 401 已经把 token 清掉了，
  // 并发回来的其余 401 若先走 token 检查就会被当成「未登录」漏下去，各弹一个错误框
  if (handlingUnauthorized) return true

  // 本来就没登录，没有「过期」可言，按普通错误走
  const token = getToken()
  if (!token) return false

  // token 还没过期却收到 401 → 不是会话过期，保留登录态，把后端的错误照常弹给用户看
  if (!tokenExpired(token)) return false

  handlingUnauthorized = true

  removeToken()
  localStorage.removeItem('USER')
  Message.error('登录已过期，请重新登录')

  // 用整页跳转而不是 router.push：顺带清掉 Vuex 里的残留用户态，
  // 也避免 request.ts 反向依赖 router 造成循环引用
  setTimeout(() => {
    window.location.href = '/login'
  }, 800)
  return true
}

axios.defaults.headers.post['Content-Type'] = 'application/json'
// 响应拦截

service.interceptors.request.use(
  config => {
    const token = getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => Promise.reject(error)
)
service.interceptors.response.use(
  response => response,
  error => {
    console.error('=== API Error ===')
    console.error('URL:', error.config?.url)
    console.error('Method:', error.config?.method)
    console.error('Status:', error.response?.status)
    console.error('Response Data:', error.response?.data)
    console.error('Error Message:', error.message)
    console.error('================')
    
    const errMsg =
      error?.response?.data?.error ||
      error?.message ||
      '请求失败，请稍后重试'

    if (error?.response?.status === 401 && handleUnauthorized()) {
      return Promise.reject(error)
    }

    // v2.0 不需要 chat/history 接口,静默处理该错误
    if (error.config?.url?.includes('/api/chat/history/')) {
      console.log('忽略历史记录接口错误 (v2.0 不需要)')
      return Promise.reject(error)
    }

    // 调用方标记了 _silentError 就不弹全局兜底框，由它自己处理（照 serviceGo 的同名出口镜像）。
    // Python 后端的错误体是裸 {message}，而上面的 errMsg 读的是 data.error，键对不上、
    // 只会弹出 axios 的兜底文案；需要给作者看精确原因的调用方应当标记 _silentError，
    // 并在自己的 catch 里读 err.response.data.message
    if (error.config?._silentError) {
      return Promise.reject(error)
    }

    MessageBox.alert(errMsg, '错误', {
      confirmButtonText: '确定',
      type: 'error'
    }).catch(() => { /* ignore cancel from overlapping MessageBox */ })

    return Promise.reject(error)
  }
)


// service_go
serviceGo.interceptors.request.use(
  config => {
    const token = getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => Promise.reject(error)
)
serviceGo.interceptors.response.use(
  response => response,
  error => {
    console.error('=== Go API Error ===')
    console.error('URL:', error.config?.url)
    console.error('Method:', error.config?.method)
    console.error('Status:', error.response?.status)
    console.error('Response Data:', error.response?.data)
    console.error('Error Message:', error.message)
    console.error('====================')

    if (error?.response?.status === 401 && handleUnauthorized()) {
      return Promise.reject(error)
    }

    // 如果调用方标记了 _silentError，不弹全局弹窗，由调用方自行处理
    if (error.config?._silentError) {
      return Promise.reject(error)
    }

    let errMsg: string
    if (error?.response?.status === 413) {
      // nginx 拦截的 413 会返回一段 HTML 错误页，避免直接展示给用户
      errMsg = '文件过大，超出服务器允许的上传大小，请联系管理员调整或更换更小的文件'
    } else {
      const rawData = error?.response?.data
      // 后端 JSON 错误优先；纯字符串错误且不像 HTML 才直接展示
      // Go 后端的错误体是 {code, message}（见 WriteErrorResponse），不读 message 就只剩
      // axios 那句 "Request failed with status code 400"，看不出真实原因
      const stringData = typeof rawData === 'string' && !/^\s*</.test(rawData) ? rawData : ''
      errMsg =
        rawData?.error ||
        rawData?.message ||
        stringData ||
        error?.message ||
        '请求失败，请稍后重试'
    }

    MessageBox.alert(typeof errMsg === 'string' ? errMsg : '请求失败，请稍后重试', '错误', {
      confirmButtonText: '确定',
      type: 'error'
    }).catch(() => { /* ignore cancel from overlapping MessageBox */ })

    return Promise.reject(error)
  }
)

export default service
